import { ChangeEvent, useMemo, useState } from "react";
import classNames from "classnames";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import {
    FormattedIdentityProviderCompanyIDPAttributeMap,
    IdentityProviderAttributeType,
    IdentityProviderAttributeTypeField
} from "@idw-types/data/identity-providers";
import { SelectOption } from "@idw-types/common";
import { InputFormGroup } from "../../common/input-form-group/input-form-group";
import { SelectFormGroup } from "../../common/select-form-group/select-form-group";
import { ButtonWithIcon } from "../../common/button-with-icon/button-with-icon";
import styles from "../identity-provider-shared-style.less";
import { resources } from "@constants/hardcoded-strings";
import { LabelWidthTypes } from "@enums/label-width-types";
import { sortStrings } from "@utils/string-util";
import { mapEntitiesWithNameAndIdToSelectOptions } from "@utils/select-option-mapping-util";

type IdentityProviderFederatedAttributeContainerProps = {
    index: number;
    idPrefix: string;
    attribute: FormattedIdentityProviderCompanyIDPAttributeMap;
    attributeTypes: Array<IdentityProviderAttributeType>;
    attributeTypeFields: Array<IdentityProviderAttributeTypeField>;
    isDisabled: boolean;
    handleIDPFieldChange: (newValue: string, index: number) => void;
    handleAttributeTypeChange: (newValue: SelectOption, index: number) => void;
    handleAttributeTypeFieldChange: (newValue: SelectOption, index: number) => void;
    handleDeleteClick: (index: number) => void;
};

export const IdentityProviderFederatedAttributeContainer = (props: IdentityProviderFederatedAttributeContainerProps) => {
    const { index, attribute, attributeTypes, attributeTypeFields, isDisabled, idPrefix, handleIDPFieldChange, handleAttributeTypeChange, handleAttributeTypeFieldChange, handleDeleteClick } = props;

    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);

    const handleIDPFieldChangeInternal = (event: ChangeEvent<HTMLInputElement>) => {
        handleIDPFieldChange(event.target.value, index);
    };

    const handleAttributeTypeChangeInternal = (newValue: SelectOption) => {
        handleAttributeTypeChange(newValue, index);
    };

    const handleAttributeTypeFieldChangeInternal = (newValue: SelectOption) => {
        handleAttributeTypeFieldChange(newValue, index);
    };

    const handleDeleteClickInternal = () => {
        handleDeleteClick(index);
    };

    const attributeTypeOptions = useMemo(() => {
        return mapEntitiesWithNameAndIdToSelectOptions(attributeTypes).sort((a, b) => sortStrings(a.label, b.label));
    }, [attributeTypes]);

    const attributeTypeFieldOptions = useMemo(() => {
        let fields: Array<SelectOption> = [];
        if (attribute.attributeTypeID) {
            fields = attributeTypeFields.filter(attributeTypeField => attributeTypeField.attributeTypeID === attribute.attributeTypeID).map(attributeTypeField => ({
                label: attributeTypeField.label,
                value: attributeTypeField.name
            }));
        }
        return fields;
    }, [attribute, attributeTypeFields]);

    const selectedAttributeType = useMemo(() => {
        if (attribute.attributeTypeID) {
            const attributeTypeOption = attributeTypeOptions.find(attributeType => attributeType.value === attribute.attributeTypeID);
            if (attributeTypeOption) {
                return attributeTypeOption;
            }
        }
        return null;
    }, [attribute, attributeTypeOptions]);

    const selectedAttributeTypeField = useMemo(() => {
        if (attribute.fieldType) {
            const attributeTypeFieldOption = attributeTypeFieldOptions.find(attributeTypeField => attributeTypeField.value === attribute.fieldType);
            if (attributeTypeFieldOption) {
                return attributeTypeFieldOption;
            }
        }
        return null;
    }, [attribute, attributeTypeFieldOptions]);

    const resource = resources.pages.identityProviderDetails.tabs.federatedAttributes;

    return (
        <div className={styles.federatedAttributeContainer}>
            <InputFormGroup
                className={styles.federatedAttributeInputFormGroup}
                controlId={`federated-attribute-key-input-${index}`}
                automationId={`federated-attribute-key-input-${index}`}
                labelWidth={labelWidth}
                placeholder={resource.inputs.idpField.placeholder}
                ariaLabel={resource.inputs.idpField.label}
                disabled={isDisabled}
                isRequired={true}
                label={resource.inputs.idpField.label}
                value={attribute.idpField || ""}
                onChange={handleIDPFieldChangeInternal}
            />
            <SelectFormGroup
                className={classNames(styles.federatedAttributeSelectFormGroup, styles.first)}
                ariaLabel={resource.inputs.attributeType.label}
                automationId={`federated-attribute-container-attribute-type-select-${index}`}
                controlId={`federated-attribute-container-attribute-type-select-${index}`}
                disabled={isDisabled}
                isClearable={false}
                isRequired={true}
                isSearchable={false}
                label={resource.inputs.attributeType.label}
                labelWidth={labelWidth}
                options={attributeTypeOptions}
                onChange={handleAttributeTypeChangeInternal}
                selectedOption={selectedAttributeType}
            />
            <SelectFormGroup
                className={styles.federatedAttributeSelectFormGroup}
                ariaLabel={resource.inputs.iddatawebField.label}
                automationId={`federated-attribute-container-attribute-field-type-select-${index}`}
                controlId={`federated-attribute-container-attribute-field-type-select-${index}`}
                disabled={isDisabled}
                isClearable={false}
                isRequired={true}
                isSearchable={false}
                label={resource.inputs.iddatawebField.label}
                labelWidth={labelWidth}
                options={attributeTypeFieldOptions}
                onChange={handleAttributeTypeFieldChangeInternal}
                selectedOption={selectedAttributeTypeField}
            />
            <ButtonWithIcon
                id={`delete-${idPrefix}-button`}
                icon={faTrash}
                title={resources.common.buttons.delete.label}
                onClick={handleDeleteClickInternal}
                disabled={isDisabled}
                variant={"outline-primary"}
                className={styles.trashIconButton}
            />
        </div>
    );
};